import React, { useState, useEffect } from 'react';
import {
  faXmark,
  faPuzzlePiece,
  faLink,
  faPen,
  faArrowsRotate,
  faTrash,
  faUsersSlash,
  faCircle,
  faEye,
  faPhoneSlash,
  faCheck,
  faRightFromBracket
} from '@fortawesome/free-solid-svg-icons';
import { faWhatsapp } from '@fortawesome/free-brands-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import IntegrationsModal from '../Integrations';
import { useTranslation } from 'react-i18next';
import { restartInstance, deleteInstanceFull } from '../../../../../../services/api';
import { apiRequest } from '../../../../../../config/api';
import SuccessModal from '../../SuccessQR/index';
import './instance.css';

const InstanceConfigModal = ({ isOpen, onClose, instanceName = "Instância Principal", onOpenIntegrations, connection, isConnected }) => {
  const [settings, setSettings] = React.useState({
    ignoreGroups: false,
    alwaysOnline: false,
    viewMessages: false,
    rejectCalls: false,
    missedCallMessage: ""
  });
  const [showIntegrations, setShowIntegrations] = React.useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareUserId, setShareUserId] = useState('');
  const [shareLoading, setShareLoading] = useState(false);
  const [shareError, setShareError] = useState('');
  const [shareSuccess, setShareSuccess] = useState('');
  const [sharedUsers, setSharedUsers] = useState([]);
  const [sharedLoading, setSharedLoading] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [newName, setNewName] = useState(connection ? (connection.name || "") : instanceName);
  const [savingName, setSavingName] = useState(false);
  const [nameError, setNameError] = useState('');
  const [showNameMsg, setShowNameMsg] = useState('');
  const [restarting, setRestarting] = useState(false);
  const [showRestartSuccess, setShowRestartSuccess] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const { t } = useTranslation();

  // Use connection data if available
  const displayName = connection ? connection.name : instanceName;

  // Buscar usuários já compartilhados ao abrir o modal de compartilhamento
  useEffect(() => {
    const fetchSharedUsers = async () => {
      if (!showShareModal || !connection?.id) {
        setSharedUsers([]);
        return;
      }
      setSharedLoading(true);
      try {
        // Busca todos os compartilhamentos dessa instância
        const data = await apiRequest(`/instances/${connection.id}/shared-users`);

        setSharedUsers(
          (data || []).map(u => ({
            id: u.viewer_id,
            nome: u.viewer_name,
            email: u.viewer_email || ''
          }))
        );
      } catch (error) {
        console.error('Erro ao buscar usuários compartilhados:', error);
        setSharedUsers([]);
      }
      setSharedLoading(false);
    };
    fetchSharedUsers();
  }, [showShareModal, connection?.id]);


  // Se não estiver aberto, não renderiza nada
  if (!isOpen) return null;

  const handleSettingToggle = (setting) => {
    setSettings(prev => ({
      ...prev,
      [setting]: !prev[setting]
    }));
  };

  const handleMessageChange = (e) => {
    setSettings(prev => ({
      ...prev,
      missedCallMessage: e.target.value
    }));
  };

  const handleIntegrationsClick = (e) => {
    e.stopPropagation();
    setShowIntegrations(true);
  };

  const handleCloseIntegrations = () => {
    setShowIntegrations(false);
  };

  const handleOpenConfigWeb = () => {
    setShowIntegrations(false);
    // Se você tiver um modal de ConfigWeb, abra aqui
    // Exemplo: setShowWebhookModal(true);
  };

  // Compartilhamento de instância
  const handleShareInstance = async () => {
    setShareError('');
    setShareSuccess('');
    if (!shareUserId) {
      setShareError('Informe o ID do usuário.');
      return;
    }
    // Não permite compartilhar consigo mesmo
    if (shareUserId === connection?.user_id) {
      setShareError('Você não pode compartilhar a instância com você mesmo.');
      return;
    }
    setShareLoading(true);
    try {
      // Não permite duplicidade
      if (sharedUsers.some(u => u.id === shareUserId)) {
        setShareError('Este usuário já possui acesso a esta instância.');
        setShareLoading(false);
        return;
      }

      // Compartilha a instância através do endpoint
      const result = await apiRequest(`/instances/${connection.id}/shared-users`, {
        method: 'POST',
        body: JSON.stringify({ viewer_id: shareUserId })
      });

      setShareSuccess('Instância compartilhada com sucesso!');
      setShareUserId('');
      // Atualiza lista de compartilhados
      setSharedUsers(prev => [
        ...prev,
        {
          id: result.viewer_id,
          nome: result.viewer_name || '',
          email: result.viewer_email || ''
        }
      ]);
    } catch (error) {
      setShareError(error.message || 'Erro ao compartilhar instância.');
    }
    setShareLoading(false);
  };

  // Função para salvar o novo nome de exibição da instância (apenas o campo 'name')
  const handleSaveName = async () => {
    setNameError('');
    if (!newName.trim()) {
      setNameError('O nome não pode ser vazio.');
      return;
    }
    setSavingName(true);
    try {
      // Atualiza apenas o campo 'name', não altera o instanceName
      await apiRequest(`/instances/${connection.id}`, {
        method: 'PUT',
        body: JSON.stringify({ name: newName })
      });

      if (connection) connection.name = newName;
      setEditingName(false);
      setShowNameMsg('Nome alterado com sucesso!');
      setTimeout(() => setShowNameMsg(''), 4000);
    } catch (error) {
      setNameError(error.message || 'Erro ao atualizar nome.');
    }
    setSavingName(false);
  };

  // Função para recriar (restart) a instância
  const handleRestartInstance = async () => {
    setRestarting(true);
    try {
      await restartInstance(
        connection?.instanceName || connection?.instance,
        connection?.rawData?.server_id || connection?.server_id
      );
      setRestarting(false);
      setShowRestartSuccess(true);
    } catch (err) {
      setRestarting(false);
    }
  };

  // Função para desconectar (logout) a instância
  const handleLogoutInstance = async () => {
    if (!window.confirm('Tem certeza que deseja desconectar esta instância do WhatsApp?')) {
      return;
    }

    setDisconnecting(true);
    try {
      const sessionName = connection?.instanceName || connection?.instance;
      const serverId = connection?.rawData?.server_id || connection?.server_id;

      await apiRequest(`/sessions/${sessionName}/logout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ server_id: serverId })
      });

      setShowNameMsg('Instância desconectada com sucesso!');
      setTimeout(() => {
        setShowNameMsg('');
        if (typeof window !== "undefined") {
          window.location.reload();
        }
      }, 2000);
    } catch (error) {
      alert('Erro ao desconectar instância: ' + (error.message || 'Erro desconhecido'));
    } finally {
      setDisconnecting(false);
    }
  };

  // Função para deletar a instância
  const handleDeleteInstance = () => {
    setDeleteError('');
    if (!connection?.id) {
      setDeleteError('ID da instância não encontrado.');
      return;
    }
    setShowDeleteModal(true);
  };

  const confirmDeleteInstance = async () => {
    setShowDeleteModal(false);
    setDeleting(true);
    try {
      await deleteInstanceFull(connection);
      setDeleting(false);
      if (typeof window !== "undefined") {
        window.location.reload(); // Atualiza a lista de instâncias
      }
      if (onClose) onClose();
    } catch (err) {
      setDeleteError('Erro ao apagar instância.');
      setDeleting(false);
    }
  };

  return (
    <>
      {/* Aviso de alteração de nome */}
      {showNameMsg && (
        <div style={{
          position: 'fixed',
          top: 24,
          right: 24,
          zIndex: 9999,
          background: '#fff',
          color: '#10b981',
          border: '1px solid #10b981',
          borderRadius: 10,
          boxShadow: '0 2px 16px 0 rgba(0,0,0,0.10)',
          padding: '18px 32px',
          fontWeight: 500,
          fontSize: 16,
          minWidth: 260,
          display: 'flex',
          alignItems: 'center',
          gap: 12
        }}>
          {showNameMsg}
        </div>
      )}

      <div className="modal-container">
        {/* Modal Backdrop */}
        <div className="modal-backdrop" onClick={onClose}></div>

        {/* Modal Content */}
        <div className="modal instance-modal">
          {/* Modal Header */}
          <div className="modal-header">
            <div className="header-content">
              <h2>{t('instance.configuration')}</h2>
            </div>
            <div className="header-actions">
              <div className="action-group">

              </div>
              <button
                className="icon-button"
                title={t('instance.delete')}
                onClick={handleDeleteInstance}
                disabled={deleting}
              >
                <FontAwesomeIcon icon={faTrash} className="text-red-400" spin={deleting} />
              </button>
              <button className="icon-button" onClick={onClose} title={t('instance.close')}>
                <FontAwesomeIcon icon={faXmark} className="text-white/70 text-xl" />
              </button>
            </div>
          </div>

          {/* Modal Body */}
          <div className="modal-body">
            {/* Instance Name Section */}
            <div className="instance-name-section">
              {editingName ? (
                <div className="name-editor">
                  <div className="name-edit-form">
                    <div className="input-group">
                      <input
                        type="text"
                        className="name-input"
                        value={newName}
                        onChange={(e) => setNewName(e.target.value)}
                        placeholder={t('instance.enterName')}
                        onKeyPress={(e) => e.key === 'Enter' && handleSaveName()}
                        autoFocus
                      />
                      {nameError && (
                        <div className="error-message">
                          <FontAwesomeIcon icon={faCircle} />
                          <span>{nameError}</span>
                        </div>
                      )}
                      <div className="input-actions">
                        <button
                          className="action-btn save-btn"
                          onClick={handleSaveName}
                          disabled={savingName}
                        >
                          {savingName ? t('common.saving') : t('common.save')}
                        </button>
                        <button
                          className="action-btn cancel-btn"
                          onClick={() => {
                            setEditingName(false);
                            setNewName(displayName);
                            setNameError('');
                          }}
                        >
                          {t('common.cancel')}
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="name-display">
                  <div className="name-content">
                    <span className="current-name">
                      <span style={{ fontWeight: 600, color: '#000000' }}>Nome da Session: </span>
                      {displayName || t('instance.noName')}
                    </span>
                  </div>
                  <button
                    className="edit-name-btn"
                    onClick={() => setEditingName(true)}
                  >
                    <FontAwesomeIcon icon={faPen} />
                    <span>{t('instance.edit')}</span>
                  </button>
                </div>
              )}
            </div>

            {/* Instance Status Section */}
            <div className="instance-status-section">
              <div className="section-header">
                <h4 className="section-title">{t('instance.status')}</h4>
                <p className="section-description">{t('instance.statusDescription')}</p>
              </div>
              
              <div className="status-card">
                <div className="status-indicator">
                  <div className={`status-dot ${isConnected ? 'connected' : 'disconnected'}`}></div>
                  <div className="status-info">
                    <span className="status-label">{t('instance.currentStatus')}</span>
                    <span className={`status-value ${isConnected ? 'connected' : 'disconnected'}`}>
                      {isConnected ? t('instance.connected') : t('instance.disconnected')}
                    </span>
                  </div>
                </div>
                <div className="status-actions">
                  <button
                    className="status-action-btn disconnect-btn"
                    onClick={handleLogoutInstance}
                    disabled={!isConnected || disconnecting}
                  >
                    <FontAwesomeIcon icon={faRightFromBracket} spin={disconnecting} />
                    <span>{disconnecting ? 'Desconectando...' : 'Desconectar'}</span>
                  </button>
                  <button
                    className="status-action-btn"
                    onClick={handleRestartInstance}
                    disabled={restarting}
                  >
                    <FontAwesomeIcon icon={faArrowsRotate} spin={restarting} />
                    <span>{restarting ? t('instance.restarting') : t('instance.recreate')}</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Settings Section */}
            <div className="settings-section">
              <div className="section-header">
                <h4 className="section-title">Configurações</h4>
                <p className="section-description">Personalize o comportamento da instância</p>
              </div>

              <div className="settings-grid">
                {/* Always Online */}
                <div className="setting-item">
                  <div className="setting-info">
                    <span className="setting-label">{t('instance.alwaysOnline')}</span>
                  </div>
                  <ToggleSwitch
                    checked={settings.alwaysOnline}
                    onChange={() => handleSettingToggle('alwaysOnline')}
                  />
                </div>

                {/* Reject Calls */}
                <div className="setting-item">
                  <div className="setting-info">
                    <span className="setting-label">{t('instance.rejectCalls')}</span>
                  </div>
                  <ToggleSwitch
                    checked={settings.rejectCalls}
                    onChange={() => handleSettingToggle('rejectCalls')}
                  />
                </div>

                {/* View Messages */}
                <div className="setting-item">
                  <div className="setting-info">
                    <span className="setting-label">{t('instance.viewMessages')}</span>
                  </div>
                  <ToggleSwitch
                    checked={settings.viewMessages}
                    onChange={() => handleSettingToggle('viewMessages')}
                  />
                </div>

                {/* Ignore Groups */}
                <div className="setting-item">
                  <div className="setting-info">
                    <span className="setting-label">{t('instance.ignoreGroups')}</span>
                  </div>
                  <ToggleSwitch
                    checked={settings.ignoreGroups}
                    onChange={() => handleSettingToggle('ignoreGroups')}
                  />
                </div>
              </div>
            </div>

            {/* Missed Call Message */}
            {settings.rejectCalls && (
              <div className="message-section">
                <label className="message-label">{t('instance.missedCallMsg')}</label>
                <textarea
                  className="message-input"
                  value={settings.missedCallMessage}
                  onChange={handleMessageChange}
                  placeholder={t('instance.placeholderMsg')}
                  rows="3"
                />
              </div>
            )}
          </div>

          {/* Modal Footer */}
          <div className="modal-footer">
            <div className="footer-buttons">
              {isConnected && (
                <button className="action-button" onClick={handleIntegrationsClick}>
                  <FontAwesomeIcon icon={faLink} className="text-blue-400" />
                  <span>{t('instance.integrations')}</span>
                </button>
              )}
              <button
                className="action-button"
                onClick={() => setShowShareModal(true)}
                type="button"
              >
                <FontAwesomeIcon icon={faPuzzlePiece} className="text-purple-400" />
                <span>{t('instance.sharing')}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

                  {showIntegrations && (
              <div style={{ position: 'fixed', inset: 0, zIndex: 9999 }}>
                <IntegrationsModal
                  onClose={handleCloseIntegrations}
            instanceName={connection?.instanceName || connection?.instance || connection?.name || displayName}
            onWebhookInstance={handleOpenConfigWeb}
            connection={connection}
          />
        </div>
      )}

      {/* Modal de Compartilhamento/Atribuição */}
      {showShareModal && (
        <div className="modal-container">
          <div className="modal-backdrop" onClick={() => setShowShareModal(false)} />
          <div className="modal share-modal">
            <div className="modal-header">
              <h3>{t('instance.shareTitle')}</h3>
              <button className="icon-button" onClick={() => setShowShareModal(false)}>
                <FontAwesomeIcon icon={faXmark} />
              </button>
            </div>
            <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <label htmlFor="user-id-input" style={{ fontWeight: 500 }}>{t('instance.userIdLabel')}</label>
              <input
                id="user-id-input"
                type="text"
                value={shareUserId}
                onChange={e => setShareUserId(e.target.value)}
                placeholder={t('instance.userIdPlaceholder')}
                style={{
                  padding: '10px 12px',
                  borderRadius: 8,
                  border: '1px solid #e5e7eb',
                  fontSize: 15,
                  marginBottom: 4
                }}
              />
              {shareError && <span style={{ color: '#ef4444', fontSize: 14 }}>{shareError}</span>}
              {shareSuccess && <span style={{ color: '#10b981', fontSize: 14 }}>{shareSuccess}</span>}
              <button
                className="action-button"
                style={{ marginTop: 8, alignSelf: 'flex-end', minWidth: 120 }}
                onClick={handleShareInstance}
                disabled={shareLoading}
              >
                {shareLoading ? t('instance.assigning') : t('instance.assignShare')}
              </button>
              <div style={{ marginTop: 24 }}>
                <div style={{ fontWeight: 600, marginBottom: 8 }}>{t('instance.sharedUsersTitle')}</div>
                {sharedLoading ? (
                  <div style={{ color: '#6b7280', fontSize: 15 }}>{t('instance.loading')}</div>
                ) : sharedUsers.length === 0 ? (
                  <div style={{ color: '#6b7280', fontSize: 15 }}>{t('instance.noUsers')}</div>
                ) : (
                  <ul style={{ paddingLeft: 16 }}>
                    {sharedUsers.map(u => (
                      <li key={u.id} style={{ color: '#374151', marginBottom: 4 }}>
                        <span style={{ fontWeight: 500 }}>{u.nome || u.email || u.id}</span>
                        <span style={{ color: '#6b7280', marginLeft: 8, fontSize: 13 }}>
                          {u.email ? `(${u.email})` : u.id}
                        </span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {showRestartSuccess && (
        <SuccessModal
          isOpen={showRestartSuccess}
          onClose={() => {
            setShowRestartSuccess(false);
            if (typeof window !== "undefined") {
              window.location.href = '/';
            }
          }}
          showQrHint={true}
          isRecreation={true}
        />
      )}

      {/* Modal de confirmação para deletar instância */}
      {showDeleteModal && (
        <div className="delete-modal-overlay">
          <div className="delete-modal-backdrop" onClick={() => setShowDeleteModal(false)}></div>
          <div className="delete-modal">
            <div className="delete-modal-content">
              <div className="delete-icon-container">
                <FontAwesomeIcon icon={faTrash} className="delete-icon" />
              </div>
              
              <h2 className="delete-title">{t('enterprise.deleteInstance.title')}</h2>
              
              <p className="delete-message">
                {t('enterprise.deleteInstance.message', { instanceName: displayName })}
              </p>
              
              <div className="delete-warning">
                <FontAwesomeIcon icon={faUsersSlash} className="warning-icon" />
                <span>{t('enterprise.deleteInstance.warning')}</span>
              </div>
              
              <div className="delete-actions">
                <button 
                  className="cancel-delete-btn" 
                  onClick={() => setShowDeleteModal(false)}
                  disabled={deleting}
                >
                  {t('enterprise.deleteInstance.cancel')}
                </button>
                <button 
                  className="confirm-delete-btn" 
                  onClick={confirmDeleteInstance}
                  disabled={deleting}
                >
                  {deleting ? t('enterprise.deleteInstance.deleting') : t('enterprise.deleteInstance.confirm')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tela de carregamento durante exclusão */}
      {deleting && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.55)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
          }}
          aria-busy="true"
          aria-live="polite"
          role="status"
        >
          <div
            style={{
              background: '#111827',
              color: '#fff',
              border: '1px solid rgba(0,255,153,0.15)',
              borderRadius: 12,
              padding: '20px 24px',
              minWidth: 260,
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              boxShadow: '0 10px 30px rgba(0,0,0,0.35)'
            }}
          >
            <FontAwesomeIcon icon={faTrash} spin style={{ color: '#10b981', fontSize: 20 }} />
            <span style={{ fontSize: 16, fontWeight: 600 }}>{t('enterprise.deleteInstance.deleting')}</span>
          </div>
        </div>
      )}
    </>
  );
};

// Componente auxiliar para o toggle switch
const ToggleSwitch = ({ checked, onChange }) => (
  <label className="toggle-switch">
    <input type="checkbox" checked={checked} onChange={onChange} />
    <span className="slider"></span>
  </label>
);

export default InstanceConfigModal;